<script setup lang="ts">
import type { Button } from '@nordhealth/components'
import type { RouteLocationPathRaw, UseLinkReturn } from 'vue-router'
import type { NuxtLinkProps } from 'nuxt/app'
import { omit as _omit } from 'lodash-es'
import { handleNuxtLinkSlotNavigation } from '@/utils/navigation.js'

defineOptions({
  inheritAttrs: false,
})

/**
 * https://nordhealth.design/components/button/#properties
 */
type ButtonProps = {
  id?: Button['id']
  type?: Button['type']
  variant?: Button['variant'] | 'text'
  size?: Button['size']
  square?: Button['square']
  expand?: Button['expand']
  disabled?: Button['disabled']
  loading?: Button['loading']
}

export type NavigationOptions = {
  /**
   * Route Location the button/link should navigate to when clicked on.
   * This prop type is set to `unknown` because it generates some TS errors due to its complex type.
   * Rely on the `NuxtLink` component to handle the proper type instead.
   */
  to?: string | RouteLocationPathRaw | Record<string, unknown> | null // helps TS understand the complex type of NuxtLinkProps['to']
  /**
   * Forces the button/link to be considered as external (true) or internal (false).
   * When provided together with `isLink` property, it will render an hyperlink with an external link icon.
   */
  external?: NuxtLinkProps['external']
  /**
   * The target attribute specifies where to open the linked document.
   * This prop is only used when `external` is true.
   */
  target?: Button['target']
}

export type Props = ButtonProps &
  NavigationOptions & {
    /**
     * Makes the the button/link styles to resemble an hyperlink text (no border, background, etc).
     * When provided together with a `to` property, it will be rendered in `color-text-link`. Otherwise, it will be rendered in `color-text-highlight` color.
     * When provided together with a `to` and `external` property, it will render an hyperlink with an external link icon.
     */
    isLink?: boolean
  } & {
    /**
     * Adding a `onClick` property to the `Props` type to override the `onClick` property of the `Button` component.
     * Reason for this is so that this component can be used in TSX components without any type errors.
     * Using a flexible function type to accept any click handler signature.
     */
    onClick?: (...args: never[]) => unknown
  }

const props = withDefaults(defineProps<Props>(), {
  to: undefined,
  external: undefined,
  id: undefined,
  type: 'button',
  variant: 'default',
  size: 'm',
  square: undefined,
  expand: undefined,
  disabled: undefined,
  loading: undefined,
  target: undefined,
  onClick: undefined,
})

const variant = computed<Button['variant']>(() => {
  // The `text` value is not a valid variant for the `nord-button`; it is merely an indicator
  // that we want the button to visually resemble a hyperlink (e.g., no border, background, etc.).
  // Since `text` is not valid, we need to set a valid variant instead.
  // When `variant` is `text` or the button functions as a link (`isLink`), we use the `default`
  // variant, which allows the necessary CSS variables to be modified for the desired
  // hyperlink appearance. For any other values, we use the provided `props.variant`.
  return props.variant === 'text' || props.isLink
    ? 'default'
    : (props.variant as Button['variant'])
})

const isCtaText = computed(() => {
  // CTA's (Call-To-Actions) are buttons for actions other than simple redirects.
  // A non-link button with the `text` variant should visually resemble a hyperlink
  // (e.g., no border or background) while maintaining its role as a CTA.
  // Style-wise, a CTA Text is identical to a hyperlink except for its color.
  return !props.isLink && props.variant === 'text'
})

const customClasses = computed(() => {
  // When there are no `customClasses`, `undefined` is returned so that
  // the `class` attribute does not get set in the element.
  if (!isCtaText.value && !props.isLink) {
    return
  }

  return {
    'is-cta-text': isCtaText.value,
    'is-link': props.isLink,
  }
})

// Get router link class names from config
const config = useRuntimeConfig()
const { activeClass, exactActiveClass } = config.public.nuxtLink || {
  activeClass: 'router-link-active',
  exactActiveClass: 'router-link-exact-active',
}

// Helper function to build router classes from NuxtLink's slot properties
const buildRouterClasses = (isActive: boolean, isExactActive: boolean) => ({
  [exactActiveClass]: isExactActive,
  [activeClass]: isActive,
})

const attrs = useAttrs() as Record<string, unknown>
const nordButtonBind = computed(() => {
  // Extracting props/attributes that are not valid for the `nord-button`.
  const invalidNordButtonProps = ['to', 'isLink', 'external']
  const validNordButtonProps = _omit(props, invalidNordButtonProps)
  return {
    ...attrs,
    ...validNordButtonProps,
    variant: variant.value,
  }
})

/**
 * Handles button clicks with navigation support and modifier key detection.
 * Opens links in new tab when Cmd/Ctrl is held during click.
 */
const handleBaseButtonClick = (
  event: MouseEvent,
  href: string,
  navigate: UseLinkReturn['navigate'],
  options: Pick<NavigationOptions, 'external' | 'target'>,
) => {
  // Check if user is holding Cmd (Mac) or Ctrl (Windows/Linux) key
  const isModifierKeyPressed = event.metaKey || event.ctrlKey

  // Create navigation options with target="_blank" for modifier key clicks
  const navigationOptions = {
    ...options,
    ...(isModifierKeyPressed && { target: '_blank' as const }),
  }

  // Use existing navigation function which already handles target="_blank"
  handleNuxtLinkSlotNavigation(href, navigate, navigationOptions)
}
</script>

<template>
  <NuxtLink
    v-if="to"
    v-slot="{ href, navigate, isActive, isExactActive }"
    :to
    :target
    :external
    custom
  >
    <nord-button
      v-bind="nordButtonBind"
      :class="[customClasses, buildRouterClasses(isActive, isExactActive)]"
      :href
      @click.prevent="
        handleBaseButtonClick($event, href, navigate, {
          external,
          target,
        })
      "
    >
      <slot name="start" />
      <slot />
      <slot name="end">
        <nord-icon
          v-if="isLink && external"
          slot="end"
          class="external-icon"
          name="interface-new-window-small"
          size="xxs"
        />
      </slot>
    </nord-button>
  </NuxtLink>

  <nord-button v-else v-bind="nordButtonBind" :class="customClasses">
    <slot name="start" />
    <slot />
    <slot name="end">
      <nord-icon
        v-if="isLink && external"
        slot="end"
        class="external-icon"
        name="interface-new-window-small"
        size="xxs"
      />
    </slot>
  </nord-button>
</template>

<style scoped>
/* Hyperlink styles – so that the `nord-button` resembles an hyperlink */
.is-cta-text,
.is-link {
  --n-button-min-block-size: fit-content;
  --n-button-min-inline-size: fit-content;
  --n-button-text-align: 'start';
  --n-button-gap: var(--n-space-xs);
  --n-button-padding-inline: 0;
  --_n-button-padding-block: 0;
  --_n-button-box-shadow: none;
  --n-button-background-color: transparent;
  --n-color-border-hover: transparent;
  --_n-button-border-color: transparent;
}

.is-cta-text:not([disabled]):hover,
.is-link:not([disabled]):hover {
  text-decoration: underline;
}

.is-cta-text :deep(nord-icon),
.is-link :deep(nord-icon) {
  color: inherit;
}

.is-cta-text[disabled],
.is-link[disabled] {
  color: var(--n-color-text-weakest);
  --n-button-color: var(--n-color-text-weakest);
  --_n-button-opacity: 1;
}

/* CTA Text styles */
.is-cta-text:not([disabled]) {
  color: var(--n-color-text-highlight);
  --n-button-color: var(--n-color-text-highlight);
}

.is-cta-text:not([disabled]):hover {
  color: var(--n-color-accent);
  --n-button-color: var(--n-color-accent);
}

/* Link Text styles */
.is-link:not([disabled]),
.is-link:not([disabled]):hover {
  color: var(--n-color-text-link);
  --n-button-color: var(--n-color-text-link);
}

/* Remove pointer events from NuxtLink's `a` tag */
a:has(nord-button[disabled]) {
  pointer-events: none;
}

/* Set the correct dropdown/popout icon color when the button is not primary or danger */
[aria-haspopup='true']:not([variant='primary'], [variant='danger'])
  :deep(nord-icon[name='interface-dropdown-small']) {
  color: var(--n-color-text-weaker);
}

[aria-haspopup='true']:not([variant='primary'], [variant='danger']):hover
  :deep(nord-icon[name='interface-dropdown-small']) {
  color: var(--n-color-text);
}
</style>
